import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_CONVERSATION_STATUS_ACTIVE,
} from '@rsos/constants/rcsStatuses';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useLazyIndicateTypingRcsConversationQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import {
  setRcsUnseenMessageStatus,
  setRcsunblurMessageUrls,
} from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { ChatContainer } from './RcsChat.styles';
import { ICON_ROW_HEIGHT } from './RcsChatBox';
import {
  RCS_CHAT_HEADER_HEIGHT,
  RCS_TYPING_INDICATOR_WINDOW,
} from './constants';

/**
 * Create a TalkJSchat session for a given caller ID
 * @param {String} callerID - phone number
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured
 * messages that drives some UI updates
 * @param {Boolean} showButtons - Flag that controls the height and max height
 * of the component
 * @param {Boolean} isRcsChatBoxVisible - Flag that controls overflowY
 * styling
 */
const RcsChat = ({
  callerID,
  showPreconfiguredMessages,
  showButtons,
  isRcsChatBoxVisible,
}) => {
  const dispatch = useDispatch();
  const chatRef = useRef();

  const [chatSession, setChatSession] = useState(null);
  const [chatbox, setChatbox] = useState(null);

  const selectedTab = useSelector(state => state.eventPane.selectedTab);
  const isRcsChatVisibleRedux = useSelector(
    state => state.eventPane.chatVisibility?.isRcsChatVisible,
  );

  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const lastTriggeredRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const chatTheme = useTalkJSTheme(false, true);
  const isVT911Enabled = useIsVT911Enabled();
  const rcsConversation = useSelectedRcsConversation(callerID);
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const unblurMessageUrls = useSelector(state => state.rcs.unblurMessageUrls);

  const [triggerIndicateTypingRcsConversation] =
    useLazyIndicateTypingRcsConversationQuery();

  const rcsConversationId = useMemo(() => {
    if (!rcsConversation || !rcsConversation.data) return;
    return rcsConversation.data.conversation_id;
  }, [rcsConversation]);

  const hasLocationsToRender = useHasLocationsToRender();

  const loggedInUser = useTF911LoggedInUser();

  const chatHeight = useMemo(() => {
    if (
      rcsConversationId &&
      isRcsChatBoxVisible &&
      !showPreconfiguredMessages
    ) {
      if (isVT911Enabled && hasLocationsToRender) {
        if (hasAccessToUniteEventPane) {
          return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT}px)`;
        }
        return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT + ICON_ROW_HEIGHT}px)`;
      }

      if (!hasLocationsToRender) {
        return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT}px)`;
      }

      if (hasAccessToUniteEventPane) {
        if (!showButtons) {
          return '100%';
        }
        return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT}px)`;
      }
      return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT + ICON_ROW_HEIGHT}px)`;
    }
    return '0';
  }, [
    rcsConversationId,
    isRcsChatBoxVisible,
    showPreconfiguredMessages,
    isVT911Enabled,
    hasLocationsToRender,
    hasAccessToUniteEventPane,
    showButtons,
  ]);

  const shouldDisplayMessageField = useMemo(() => {
    const isAccepted =
      rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_ACCEPTED;
    const isActive =
      rcsConversation?.data?.conversation_status ===
      RCS_CONVERSATION_STATUS_ACTIVE;
    return isAccepted && isActive;
  }, [rcsConversation]);

  const triggerTypingIndicator = useCallback(() => {
    const now = Date.now();
    if (now - lastTriggeredRef.current >= RCS_TYPING_INDICATOR_WINDOW) {
      lastTriggeredRef.current = now;
      triggerIndicateTypingRcsConversation({ callerID, orgName });
    }
  }, [callerID, orgName, triggerIndicateTypingRcsConversation]);

  const handleTyping = useCallback(() => {
    if (!hasStartedTyping) {
      setHasStartedTyping(true);
      triggerTypingIndicator();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setHasStartedTyping(false);
      typingTimeoutRef.current = null;
    }, RCS_TYPING_INDICATOR_WINDOW);
  }, [triggerTypingIndicator, hasStartedTyping]);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: {
            name: chatTheme,
            custom: {
              app: 'rcs',
            },
          },
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type Message',
          },
          showTranslationToggle: false,
          presence: {
            visible: false,
          },
          captureKeyboardEvents: true,
        });

        chat.mount(chatRef.current);

        setChatSession(session);
        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
  }, [chatTheme, loggedInUser]);

  useEffect(() => {
    if (chatbox && chatSession && rcsConversationId) {
      const conversation =
        chatSession.getOrCreateConversation(rcsConversationId);
      conversation.setParticipant(createTalkUser(loggedInUser));

      conversation.setAttributes({
        custom: { unblurMessageUrls: JSON.stringify(unblurMessageUrls) },
      });

      chatbox.select(conversation);
    }
  }, [
    chatSession,
    chatbox,
    rcsConversationId,
    loggedInUser,
    unblurMessageUrls,
  ]);

  useEffect(() => {
    if (chatbox && rcsConversationId) {
      chatbox.messageField.setVisible(shouldDisplayMessageField);

      chatbox.onBlur(() => {
        chatbox.setPresence({ visible: false });
      });

      chatbox.onFocus(() => {
        chatbox.setPresence({ visible: true });
      });

      chatbox.onKeydown(e => {
        if (e.key !== 'Enter') {
          handleTyping();
        }
      });

      chatbox.onCustomMessageAction('toggleBlur', event => {
        const attachment = event?.message?.attachment;
        if (!attachment) return;

        if (unblurMessageUrls.includes(attachment.url)) {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: unblurMessageUrls.filter(url => url !== url),
            }),
          );
        } else {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: [...unblurMessageUrls, attachment.url],
            }),
          );
        }
      });
    }
  }, [
    chatSession,
    chatbox,
    rcsConversationId,
    loggedInUser,
    shouldDisplayMessageField,
    handleTyping,
    unblurMessageUrls,
    dispatch,
  ]);

  // Get separate unread tracking to check if we need to clear unread status
  const rcsUnreadMessages = useSelector(
    state => state.rcs?.unreadMessages || {},
  );
  const hasUnreadMessage =
    rcsUnreadMessages[callerID] || rcsConversation?.ui?.unseenMessage;

  useEffect(() => {
    // use redux state for visibility, but fall back to prop for backward compatibility
    const chatVisible =
      isRcsChatVisibleRedux !== undefined
        ? isRcsChatVisibleRedux
        : isRcsChatBoxVisible;

    if (
      selectedTab === CHAT_TAB &&
      chatVisible &&
      rcsConversationId &&
      chatSession &&
      chatbox &&
      isRcsChatBoxVisible &&
      hasUnreadMessage
    ) {
      dispatch(setRcsUnseenMessageStatus({ callerID, unseenMessage: false }));
    }
  }, [
    selectedTab,
    isRcsChatVisibleRedux,
    rcsConversationId,
    chatSession,
    chatbox,
    isRcsChatBoxVisible,
    hasUnreadMessage,
    rcsUnreadMessages,
    rcsConversation,
    callerID,
    dispatch,
  ]);

  const containerMaxHeight = useMemo(() => {
    if (showButtons && !rcsConversationId) {
      return 0;
    } else if (showButtons && isVT911Enabled) {
      if (hasAccessToUniteEventPane) {
        return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT}}px)`;
      }
      return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT} - ${ICON_ROW_HEIGHT}px)`;
    }
  }, [
    rcsConversationId,
    hasAccessToUniteEventPane,
    isVT911Enabled,
    showButtons,
  ]);

  return (
    <ChatContainer
      height={chatHeight}
      maxHeight={containerMaxHeight}
      overflowY={!isRcsChatBoxVisible ? 'hidden' : 'auto'}
      showBorder={isRcsChatBoxVisible && rcsConversationId}
      ref={chatRef}
    />
  );
};
export default RcsChat;
